import { default as axiosBase, AxiosRequestConfig, AxiosResponse } from 'axios'

// Extend AxiosRequestConfig to support skipping the global error toast
declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorToast?: boolean
  }
}
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { getCookieOptions } from './cookie-utils'
import { getLocalizedRouteFromKeys } from './utils'
import config from '@/config'
import { useMetadataStore, useLogoutStore } from '@/store'

// Global timezone and locale storage
let currentUserTimezone: string | null = null
let currentUserLocale: string | null = null
let isOnProtectedRoute = false

export const setCurrentUserTimezone = (timezone: string | null) => {
  currentUserTimezone = timezone
}

export const setCurrentUserLocale = (locale: string | null) => {
  currentUserLocale = locale
}

export const setIsOnProtectedRoute = (value: boolean) => {
  isOnProtectedRoute = value
}

// All requests must go through /api/ proxy (client-side)
// On server-side, go directly to the backend to avoid proxy cookie issues
const getBaseURL = () => {
  if (typeof window === 'undefined') {
    return `${config.API_BASE_URL}/`
  }
  return '/api/'
}

const axios = axiosBase.create({
  baseURL: getBaseURL(),
  timeout: config.UPLOAD_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

axios.interceptors.request.use(async config => {
  let token: string | undefined

  if (typeof window === 'undefined') {
    // Server-side: read token and forward client headers from next/headers
    try {
      const { cookies, headers } = await import('next/headers')
      const cookieStore = await cookies()
      token = cookieStore.get('accessToken')?.value

      // Forward real client IP and User-Agent to the backend
      const requestHeaders = await headers()
      const xRealIp = requestHeaders.get('x-real-ip')
      const xForwardedFor = requestHeaders.get('x-forwarded-for')
      const userAgent = requestHeaders.get('user-agent')

      if (xRealIp) {
        config.headers['X-Real-IP'] = xRealIp
      }
      if (xForwardedFor) {
        config.headers['X-Forwarded-For'] = xForwardedFor
      }
      if (userAgent) {
        config.headers['User-Agent'] = userAgent
      }
    } catch {
      // Not in a server request context (e.g., static generation)
    }
  } else {
    // Client-side: read token from js-cookie
    token = Cookies.get('accessToken')
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Add timezone header if available
  if (currentUserTimezone) {
    config.headers['X-Timezone'] = currentUserTimezone
  }

  // Add an Accept-Language header if locale is available
  if (currentUserLocale) {
    config.headers['Accept-Language'] = currentUserLocale
  }

  return config
})

let isRefreshing = false

type FailedRequest = {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

const triggerSessionExpired = () => {
  if (typeof window === 'undefined') {
    return
  }

  useLogoutStore.getState().forceLogout()

  if (!isOnProtectedRoute) {
    return
  }

  const metadata = useMetadataStore.getState().metadata
  const routeParams = metadata?.routeParams ?? {}
  const backendLocale = currentUserLocale ?? metadata?.generalSettings.appDefaultLocale ?? 'en-US'
  const localeSegment = window.location.pathname.split('/')[1] || backendLocale.toLowerCase()
  const loginPath = getLocalizedRouteFromKeys('/login', backendLocale, routeParams)
  const next = `${window.location.pathname}${window.location.search}`

  window.location.replace(`/${localeSegment}${loginPath}?next=${encodeURIComponent(next)}`)
}

const createSessionExpiredError = () => {
  const metadata = useMetadataStore.getState().metadata
  const backendLocale = currentUserLocale ?? metadata?.generalSettings.appDefaultLocale ?? 'en-US'
  const message = backendLocale.toLowerCase().startsWith('tr')
    ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.'
    : 'Session expired. Please log in again.'

  const error = new Error(message) as Error & {
    response?: { status: number; data: { message: string } }
    skipGlobalErrorToast?: boolean
  }

  error.response = {
    status: 401,
    data: { message }
  }
  error.skipGlobalErrorToast = true

  return error
}

axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: any) => {
    const originalRequest: AxiosRequestConfig & { _retry?: boolean } = error.config

    // Handle 400+ errors (except 401 and 404) - show toast if a message exists
    // 401 is handled separately below (token refresh flow)
    if (
      error.response?.status >= 400 &&
      error.response?.status !== 401 &&
      error.response?.status !== 404 &&
      !originalRequest.skipGlobalErrorToast
    ) {
      const message = error.response?.data?.message || error.response?.data?.error?.message

      if (message && typeof window !== 'undefined') {
        toast.error(message)
      }
    }

    // Handle 403 Forbidden errors - show full-page access denied
    if (error.response?.status === 403) {
      // Trigger full-page 403 error display
      if (typeof window !== 'undefined') {
        // Dispatch custom event for global error handling
        window.dispatchEvent(
          new CustomEvent('api-forbidden-error', {
            detail: { error, url: originalRequest.url }
          })
        )
      }
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // If the refresh endpoint itself returned 401, bail out immediately to avoid
      // an infinite loop / deadlock (isRefreshing stays true, queue never drains).
      if (originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error)
      }

      const refreshToken = Cookies.get('refreshToken')
      if (!refreshToken) {
        const sessionExpiredError = createSessionExpiredError()
        triggerSessionExpired()
        return Promise.reject(sessionExpiredError)
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`
            }
            return axios(originalRequest)
          })
          .catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
          refreshToken: refreshToken
        })
        const newToken = data.accessToken
        const newRefreshToken = data.refreshToken

        // Read rememberMe preference from its dedicated cookie
        const rememberMe = Cookies.get('rememberMe') === 'true'
        const cookieOptions = getCookieOptions(rememberMe)

        Cookies.set('accessToken', newToken, cookieOptions)
        Cookies.set('refreshToken', newRefreshToken, cookieOptions)

        axios.defaults.headers.Authorization = `Bearer ${newToken}`
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` }
        processQueue(null, newToken)

        return axios(originalRequest)
      } catch {
        const sessionExpiredError = createSessionExpiredError()
        processQueue(sessionExpiredError, null)
        triggerSessionExpired()
        return Promise.reject(sessionExpiredError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default axios
